import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportConversion } from '../utils/gtag';
import { BuildingOffice2Icon, EnvelopeIcon, PhoneIcon, ClockIcon, UserIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

// Simple front-end validation & mock submit. Replace with real API endpoint later.

const initialState = { category: '', name: '', phone: '', email: '', message: '' };

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorBanner, setErrorBanner] = useState('');

  const categories = [
    t('contact.categories.general'),
    t('contact.categories.service'),
    t('contact.categories.detailing'),
    t('contact.categories.diagnostics'),
    t('contact.categories.other')
  ];

  function validate(values) {
    const e = {};
    if (!values.name.trim()) e.name = t('contact.form.validation.name');
    if (!values.email.trim()) e.email = t('contact.form.validation.email');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = t('contact.form.validation.emailInvalid');
    if (!values.phone.trim()) e.phone = t('contact.form.validation.phone');
    if (!values.message.trim()) e.message = t('contact.form.validation.message');
    return e;
  }

  function handleChange(ev) {
    const { name, value } = ev.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setErrorBanner('');
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    try {
      setSubmitting(true);
      const payload = {
        access_key: 'dc7eecc0-cd5e-4b79-8d0c-adf90cc4cc0a',
        subject: `New ${form.category || 'General Inquiry'} from ${form.name}`,
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        message: form.message,
        // Optional: send to multiple recipients via redirect rule on Web3Forms dashboard
        // Here we include both addresses in the message body for visibility
        recipients: 'info@vigorautorepair.com, cinideep@gmail.com',
      };

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setForm(initialState);
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      setErrorBanner(t('contact.form.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <SEO
        title="Contact Vigor Auto - Car Garage in Ras Al Khor Dubai | Get Directions & Info"
        description="Contact Vigor Auto car garage in Ras Al Khor Dubai. Visit our workshop, call +971568699800, or WhatsApp us. Serving Nad Al Hamar, Al Awir & International City. Free pickup/dropoff available. Get directions, hours & expert auto service info."
        keywords="car garage Ras Al Khor contact, auto service centre Ras Al Khor, car repair Ras Al Khor directions, car garage dubai location, auto workshop Ras Al Khor, car service Nad Al Hamar, car repair Al Awir, car garage International City, car service centre near me, auto repair dubai contact"
      />
      <div className="bg-white">
        {/* Header */}
  <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,#F5D313,transparent_60%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 relative">
            <div className="text-center">
              <p className="uppercase tracking-widest text-xs font-semibold text-[#F5D313]/80">{t('contact.badge')}</p>
              <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-white">{t('contact.title')}</h1>
              <p className="mt-6 max-w-2xl mx-auto text-slate-200 text-base md:text-lg leading-relaxed">
                {t('contact.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Details & Form */}
  <section className="py-10 sm:py-14 md:py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
            
            {/* Left Column: Contact Info & Map */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight">{t('contact.info.title')}</h2>
                <p className="mt-3 text-slate-600 text-lg">{t('contact.info.subtitle')}</p>
                <div className="mt-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5D313]/10 rounded-lg flex items-center justify-center">
                      <BuildingOffice2Icon className="w-6 h-6 text-[#F5D313]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{t('contact.location.title')}</h3>
                      <p className="text-slate-600">{t('footer.location')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5D313]/10 rounded-lg flex items-center justify-center">
                      <EnvelopeIcon className="w-6 h-6 text-[#F5D313]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{t('contact.email.title')}</h3>
                      <a className="text-slate-600 hover:text-[#F5D313] hover:underline transition" href="mailto:info@vigorautorepair.com">info@vigorautorepair.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5D313]/10 rounded-lg flex items-center justify-center">
                      <PhoneIcon className="w-6 h-6 text-[#F5D313]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{t('contact.phone.title')}</h3>
                      <div className="flex flex-col">
                        <a className="text-slate-600 hover:text-[#F5D313] hover:underline transition" dir="ltr" href="tel:+971568699800" onClick={() => reportConversion('tel:+971568699800')}>+971 56 8699 800</a>
                        <a className="text-slate-600 hover:text-[#F5D313] hover:underline transition" dir="ltr" href="tel:+971524333690" onClick={() => reportConversion('tel:+971524333690')}>+971 52 433 3690</a>
                      </div>
                    </div>
                  </div>
                   <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5D313]/10 rounded-lg flex items-center justify-center">
                      <ClockIcon className="w-6 h-6 text-[#F5D313]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{t('contact.hours.title')}</h3>
                      <p className="text-slate-600">{t('contact.hours.value')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight">{t('contact.map.title')}</h2>
                {/* Wrapped map in anchor so clicking opens Google Maps deep link */}
                <a
                  href="https://maps.app.goo.gl/TUp25rLZUxxKZjAn8"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open location in Google Maps"
                  className="group block mt-6 rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg relative aspect-[16/9]"
                >
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.594314969483!2d55.36357367599139!3d25.18314177772089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f67c26539429b%3A0x4f189c6d1adf2198!2sVigor%20Auto%20Repair%20Garage!5e0!3m2!1sen!2sae!4v1696169913385!5m2!1sen!2sae"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Vigor Auto Care Location"
                  ></iframe>
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors" />
                  <div className="absolute bottom-2 right-2 bg-slate-900/80 text-[10px] sm:text-xs text-white px-3 py-1 rounded-full backdrop-blur-sm flex items-center gap-1 opacity-80 group-hover:opacity-100 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                      <path d="M12.293 2.293a1 1 0 0 1 1.414 0l4 4A1 1 0 0 1 17 8h-3a2 2 0 0 0-2 2v3a1 1 0 1 1-2 0v-3a4 4 0 0 1 4-4h1.586l-2.293-2.293a1 1 0 0 1 0-1.414Z" />
                      <path d="M8 4a4 4 0 0 0-4 4v5.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4A1 1 0 0 1 2 16v-8a6 6 0 0 1 10.712-3.712 1 1 0 1 1-1.53 1.304A4 4 0 0 0 8 4Z" />
                    </svg>
                    <span>{t('contact.map.openMaps')}</span>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-2xl p-6 sm:p-8 lg:p-10">
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight">{t('contact.form.title')}</h2>
              <p className="mt-3 text-slate-600">{t('contact.form.subtitle')}</p>
              {errorBanner && (
                <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {errorBanner}
                </div>
              )}
              <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-6 relative">
                <div className="absolute -right-10 -top-10 w-52 h-52 bg-[#F5D313]/10 rounded-full" />
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700 mb-2">{t('contact.form.category')}</label>
                    <div className="relative">
                      <select name="category" value={form.category} onChange={handleChange} className="peer w-full rounded-xl border-slate-200 focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur px-3 py-2.5 shadow-sm">
                        <option value="">{t('contact.form.categoryPlaceholder')}</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700 mb-2">{t('contact.form.name')} *</label>
                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="name" value={form.name} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.name ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder={t('contact.form.namePlaceholder')} />
                    </div>
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700 mb-2">{t('contact.form.phone')} *</label>
                    <div className="relative">
                      <PhoneIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="phone" value={form.phone} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.phone ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder={t('contact.form.phonePlaceholder')} />
                    </div>
                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700 mb-2">{t('contact.form.email')} *</label>
                    <div className="relative">
                      <EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input name="email" type="email" value={form.email} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.email ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm`} placeholder={t('contact.form.emailPlaceholder')} />
                    </div>
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-slate-700 mb-2">{t('contact.form.message')} *</label>
                  <div className="relative">
                    <ChatBubbleLeftRightIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <textarea name="message" rows={5} value={form.message} onChange={handleChange} className={`w-full rounded-xl pl-10 pr-3 py-2.5 border ${errors.message ? 'border-red-400' : 'border-slate-200'} focus:ring-2 focus:ring-offset-0 focus:ring-[#F5D313] focus:border-[#F5D313] text-sm bg-white/70 backdrop-blur shadow-sm resize-y transition`} placeholder={t('contact.form.messagePlaceholder')} />
                  </div>
                  {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                  <p className="text-xs text-slate-500">{t('contact.form.required')}</p>
                  <button type="submit" disabled={submitting} className="inline-flex items-center justify-center rounded-lg bg-[#F5D313] px-8 py-3 text-sm font-bold text-slate-900 shadow-lg shadow-[#F5D313]/20 hover:bg-[#e4c40f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F5D313] disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                    {submitting ? t('contact.form.submitting') : t('contact.form.submit')}
                  </button>
                </div>
                {submitted && (
                  <div className="mt-4 rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800 font-medium">
                    <strong>{t('contact.form.success')}</strong>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* CTA Bottom - Can be removed or kept. Let's keep it for now. */}
  <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold">{t('contact.cta.title')}</h2>
            <p className="mt-4 max-w-2xl mx-auto text-slate-300 text-sm md:text-base">{t('contact.cta.subtitle')}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+971568699800" onClick={() => reportConversion('tel:+971568699800')} className="inline-flex justify-center rounded-lg bg-[#F5D313] px-6 py-3 font-medium text-slate-900 hover:bg-[#e4c40f] transition-colors shadow">{t('contact.cta.call')}</a>
              <a href="mailto:info@vigorautorepair.com" className="inline-flex justify-center rounded-lg border border-slate-300 px-6 py-3 font-medium text-white hover:bg-white/10 transition-colors shadow-sm">{t('contact.cta.email')}</a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
